import { useState, useEffect } from "react";
import { mutate } from "swr";
import { Link } from "@mui/material";
import PagePagination from "../../../components/PagePagination";
import useGlobalContext from "../../../hooks/useGlobalContext";
import ActionButtons from "../../../components/common/ActionButtons";
import localize from "../../../common/localize";
import {
  generateNoDataValues,
  getCurrentDaysForEvent,
  getExpectedSolutionDateUI,
  getOperationalStatusNameFromId,
  getOperationalStatusStyleName,
  getValueInCurrency,
  OperationalStatusEnum,
} from "../../../common/utils";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";
import ErrorMessage from "../../../components/common/ErrorMessage";
import {
  getClearEventModel,
  getCurrencyFromSetup,
} from "../../../common/EntitiesManagement";
import TableComponent from "../../../components/common/TableComponent";
import { updateEntity } from "../../../context/GlobalContext";
import NoDataLabel from "../../../components/common/NoDataLabel";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";
import { BackForeColor } from "../../../components/common/BackForeColor";
import ModalBackground from "../../../components/common/ModalBackground";
import SetServiceValues from "./SetServiceValues";
import PaymentIcon from "./PaymentIcon";
import BillIcon from "./BillIcon";
import TotalValueItem from "./TotalValueItem";
import EventSummary from "./EventSummary";
import { Print } from "@mui/icons-material";
import ReportSetup from "./report/Reportsetup";
import EventAssignmentModalButton from "./EventAssignmentModalButton";
import WarningErrorStatus from "../../../components/common/WarningErrorStatus";

export default function EventsList() {
  const {
    eventsData,
    EVENTS_API,
    config,
    pageIndexEvents,
    setPageIndexEvents,
    toggleVisibleModalConfirm,
    setShowEditEventModal,
    setEventModel,
    setShowAssignEventModal,
    eventModel,
  } = useGlobalContext();
  const [showGenerateInformModal, setShowGenerateInformModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [eventsDataFiltered, setEventsDataFiltered] = useState();
  const [deleteReason, setDeleteReason] = useState("");
  const [showServiceValuesModal, setShowServiceValuesModal] = useState(false);
  const [showEventSummary, setShowEventSummary] = useState(false);

  const getItemCode = (eventInfo) =>
    eventInfo.Code && eventInfo.Code !== ""
      ? eventInfo.Code
      : `${localize.app_name}-${eventInfo.Id}`;

  useEffect(() => {
    const reversedList = eventsData?.Data;
    setEventsDataFiltered(
      reversedList?.map((event) => ({
        ...event,
        Code: getItemCode(event),
      }))
    );
  }, [eventsData]);

  const validateOperationResult = (result, isDelete) => {
    setEventModel(getClearEventModel());
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([EVENTS_API, config]);
    if (isDelete) {
      if (pageIndexEvents !== 0 && eventsData.Data.length === 1) {
        setPageIndexEvents(pageIndexEvents - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId, reason) => {
    const deleteResult = await updateEntity(
      `${`${EVENTS_API}/${itemId}`}`,
      {
        CancelationReason: reason,
      },
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
    setDeleteReason("");
  };

  const canModifyEvent = (operationalStatusId) =>
    operationalStatusId > 0 && operationalStatusId < 3;

  const canEditServiceValue = (eventData) => {
    if (eventData) {
      return !eventData.PaymentDate; // && eventData.CanEdit === 1;
    }

    return false;
  };

  const UpdateServiceValues = async () => {
    const updateServiceResult = await updateEntity(
      `${`${EVENTS_API}/${eventModel.Id}/serviceValues`}`,
      {
        executionFinalValue: eventModel.executionFinalValue,
        sparePartsFinalValue: eventModel.sparePartsFinalValue,
      },
      config
    );

    validateOperationResult(updateServiceResult, false);
  };

  return (
    <div>
      {showServiceValuesModal && (
        <ModalBackground
          isDrawerMode
          showModal={showServiceValuesModal}
          setShowModal={setShowServiceValuesModal}
          title={`${localize.set_service_values} # ${eventModel.Id} (${eventModel.Code})`}
          onClickToCancel={() => {
            setShowServiceValuesModal(false);
          }}
          onClickToSubmit={() => {
            setShowServiceValuesModal(false);
            UpdateServiceValues();
          }}
          Component={
            <SetServiceValues
              eventModel={eventModel}
              setEventModel={setEventModel}
            />
          }
        />
      )}

      {showEventSummary && (
        <ModalBackground
          showModal={showEventSummary}
          setShowModal={setShowEventSummary}
          title={localize.event_summary}
          okButtonText={localize.view_details}
          onClickToSubmit={() => {
            setShowEventSummary(false);
            window.open(`/events/detail/${eventModel.Id}`, "_blank");
          }}
          cancelButtonText={localize.close}
          onClickToCancel={() => {
            setShowEventSummary(false);
          }}
          Component={<EventSummary eventInfo={eventModel} />}
        />
      )}

      {showGenerateInformModal && (
        <ReportSetup
          locationHref={`${window.location.href
            .split("?")[0]
            .replaceAll("#", "")}/detail/${eventModel.Id}`}
          showGenerateInformModal={showGenerateInformModal}
          setShowGenerateInformModal={setShowGenerateInformModal}
        />
      )}

      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      <DeleteConfirmationMessage
        entityName={`${localize.code}: ${eventModel.Code}`}
        message={localize.event_cancelation_message}
        title={localize.cancel_event}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setDeleteReason("");
          setEventModel(getClearEventModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(eventModel.Id, deleteReason);
        }}
        setDeleteReason={setDeleteReason}
        deleteReason={deleteReason}
        fieldTitle={localize.cancelation_reason}
      />

      <TableComponent
        noPadding
        pagination={
          <PagePagination
            pageIndex={pageIndexEvents}
            setPageIndex={setPageIndexEvents}
            dataArray={eventsData}
          />
        }
        firstColumnTitle={" "}
        columnTitles={[
          localize.actions,
          localize.code,
          // localize.operational_area,
          localize.event_type,
          localize.operational_status,
          localize.execution_status,
          localize.proceeding,
          localize.assigned_to,
          localize.total_value,
          localize.real_income,
          localize.payed,
          localize.billed,
          localize.expected_attention_date,
          localize.client_name,
          localize.address,
          localize.department,
          localize.city,
          // localize.municipality,
          // localize.sidewalk,
          // localize.vehicle,
          // localize.driver,
          localize.modified_by,
          localize.creation_date,
          // localize.modification_date,
        ]}
        results={
          eventsData.Data?.length !== 0
            ? eventsDataFiltered?.map((item) => ({
                key: item.Id,
                data: [
                  <WarningErrorStatus
                    currentValue={getCurrentDaysForEvent(item)}
                    limitForWarning={item?.EventType?.SemaphoreYellow}
                    limitForError={item?.EventType?.SemaphoreRed}
                    isSucceeded={
                      item?.OperationalStatus ===
                        OperationalStatusEnum.Finished ||
                      item?.OperationalStatus ===
                        OperationalStatusEnum.PendingReview
                    }
                    isFailed={
                      item?.OperationalStatus ===
                        OperationalStatusEnum.NotFinished ||
                      item?.OperationalStatus === OperationalStatusEnum.Canceled
                    }
                  />,
                  <div>
                    <ActionButtons
                      buttons={[
                        {
                          name: localize.print_service_order,
                          icon: (
                            <Print style={{ width: "16px", height: "16px" }} />
                          ),
                          action: () => {
                            setEventModel(item);
                            setShowGenerateInformModal(true);
                          },
                        },
                      ]}
                      editButtonText={localize.edit}
                      editLink={`/events/${item.Id}`}
                      deleteButtonText={localize.cancel}
                      detailsButtonText={localize.event_summary}
                      onClickForDetailButton={() => {
                        setEventModel(item);
                        setShowEventSummary(true);
                      }}
                      onClickForEditButton={() => {
                        setEventModel({
                          ...item,
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                          TeamId: item.AssignedTeam?.Id,
                          EventTypeId: item.EventType?.Id,
                          ClientId: item.Client?.Id,
                          StatusId: item.CurrentStatus?.Id,
                          ExpectedSolutionDate: item.ExpectedSolutionDate,
                          ExpectedSolutionDateValue: !item?.ExpectedSolutionDate
                            ? null
                            : new Date(item?.ExpectedSolutionDate),
                          ExpectedSolutionFinalDateValue:
                            !item?.ExpectedSolutionFinalDate
                              ? null
                              : new Date(item?.ExpectedSolutionFinalDate),
                          ExpectedSolutionFinalDate:
                            item?.ExpectedSolutionFinalDate,
                          PersonalId: item.PersonalAssigned?.id,
                          VehicleId: item.VehicleAssigned?.Id,
                          ProceedingId: item.ProceedingInfo?.Id,
                          AdditionalFieldValue: item.AdditionalFields
                            ? JSON.parse(item.AdditionalFields)
                            : [],
                        });
                        setShowEditEventModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setEventModel({
                          ...item,
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                          TeamId: item.AssignedTeam?.Id,
                          EventTypeId: item.EventType?.Id,
                          ClientId: item.Client?.Id,
                          StatusId: item.CurrentStatus?.Id,
                          ProceedingId: item.ProceedingInfo?.Id,
                        });
                        toggleVisibleModalConfirm();
                      }}
                      hideEditButton={!canModifyEvent(item.OperationalStatus)}
                      hideDeleteButton={!canModifyEvent(item.OperationalStatus)}
                      showCancelIcon
                    />
                  </div>,
                  <Link
                    color="primary"
                    underline="always"
                    href="#"
                    onClick={() => {
                      window.open(`/events/detail/${item.Id}`, "_blank");
                    }}
                  >{`${item.Code}`}</Link>,
                  /* <BackForeColor
                    backColor={item.EventType?.operationalArea?.BackgroundColor}
                    foreColor={item.EventType?.operationalArea?.ForegroundColor}
                    text={item.EventType?.operationalArea?.Name}
                  />, */
                  <BackForeColor
                    backColor={item.EventType.BackgroundColor}
                    foreColor={item.EventType.ForegroundColor}
                    text={item.EventType.Name}
                  />,
                  <p
                    style={{ borderRadius: "40px" }}
                    className={`rounded p-2 ${getOperationalStatusStyleName(
                      item.OperationalStatus
                    )}`}
                  >
                    {getOperationalStatusNameFromId(item.OperationalStatus)}
                  </p>,
                  <BackForeColor
                    backColor={
                      item.OperationalStatus === 0
                        ? "white"
                        : item.CurrentStatus?.BackgroundColor
                    }
                    foreColor={
                      item.OperationalStatus === 0
                        ? "red"
                        : item.CurrentStatus?.ForegroundColor
                    }
                    text={
                      item.OperationalStatus === 0
                        ? localize.cancelled
                        : item.CurrentStatus?.Name
                    }
                    styleName={
                      item.OperationalStatus === 0
                        ? "cancelled_operational_status"
                        : ""
                    }
                    TooltipText={
                      item.OperationalStatus === 0
                        ? item.FinishObservation
                        : undefined
                    }
                  />,
                  <ValueOrNoDataLabel data={item.ProceedingInfo?.Name} />,
                  !item.PersonalAssigned?.SurName ? (
                    !canModifyEvent(item.OperationalStatus) ? (
                      <NoDataLabel />
                    ) : (
                      <button
                        onClick={() => {
                          setEventModel({
                            ...item,
                            StateId: item.State.Id,
                            CityId: item.City.Id,
                            TeamId: item.AssignedTeam?.Id,
                            EventTypeId: item.EventType?.Id,
                            ClientId: item.Client?.Id,
                            StatusId: item.CurrentStatus?.Id,
                            ExpectedSolutionDateValue:
                              !item?.ExpectedSolutionDate
                                ? null
                                : new Date(item?.ExpectedSolutionDate),
                            ExpectedSolutionDate: item?.ExpectedSolutionDate,
                            ExpectedSolutionFinalDateValue:
                              !item?.ExpectedSolutionFinalDate
                                ? null
                                : new Date(item?.ExpectedSolutionFinalDate),
                            ExpectedSolutionFinalDate:
                              item?.ExpectedSolutionFinalDate,
                            PersonalId: item.PersonalAssigned?.Id,
                            VehicleId: item.VehicleAssigned?.Id,
                            ProceedingId: item.ProceedingInfo?.Id,
                          });
                          setShowAssignEventModal(true);
                        }}
                        className="not_assigned_label rounded p-2"
                      >
                        {localize.assign}
                      </button>
                    )
                  ) : canModifyEvent(item.OperationalStatus) ? (
                    <EventAssignmentModalButton
                      eventDetailData={item}
                      setEventModel={setEventModel}
                      setShowAssignEventModal={setShowAssignEventModal}
                      useParagrah
                    />
                  ) : (
                    <p>{`${item.PersonalAssigned.SurName} ${item.PersonalAssigned.LastName}`}</p>
                  ),
                  <TotalValueItem
                    item={item}
                    canEditServiceValue={canEditServiceValue}
                    setEventModel={setEventModel}
                    setShowServiceValuesModal={setShowServiceValuesModal}
                  />,
                  <ValueOrNoDataLabel
                    data={getValueInCurrency(
                      getCurrencyFromSetup(),
                      item.realIncome ?? 0
                    )}
                  />,
                  <PaymentIcon item={item} />,
                  <BillIcon item={item} />,
                  getExpectedSolutionDateUI(item),
                  <ValueOrNoDataLabel data={item.Client?.Name} />,
                  <ValueOrNoDataLabel
                    data={
                      item.Address
                        ? `${item.Address} ${item.AddressComplement ?? ""}`
                        : item.Address
                    }
                  />,
                  <ValueOrNoDataLabel data={item.State.Name} />,
                  <ValueOrNoDataLabel data={item.City.Name} />,
                  /* item.VehicleAssigned ? (
                    <p>{`(${item.VehicleAssigned.VehicleType.Name}) ${item.VehicleAssigned.Identification}`}</p>
                  ) : (
                    <NoDataLabel />
                  ), */
                  // <ValueOrNoDataLabel data={item.Driver} />,
                  item.CreatedBy,
                  /* item.ReporterName ? (
                    item.ReporterName
                  ) : (
                    <NoDataLabel text={localize.anonym} />
                  ), */
                  // <ValueOrNoDataLabel data={item.Municipality?.Name} />,
                  // <ValueOrNoDataLabel data={item.Sidewalk?.Name} />,
                  new Date(item.CreationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  /* new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  }), */
                ],
              }))
            : [generateNoDataValues(18)]
        }
      />
    </div>
  );
}
